import { Inject, Injectable, PLATFORM_ID, inject } from "@angular/core";
import { AppConfigService } from "@citi-gcg-173875/citi-utils";
import { OfferUtilService, PqpsUtilService } from "src/app/common/services";
import { MktEnvironmentService } from "src/app/core/services";
import { isPlatformBrowser } from "@angular/common";
import { AdaFriendlyNamePipe } from "src/app/common/pipes/ada-friendly-name.pipe";

@Injectable({
    providedIn: 'root'
})
export class MppRedesignService {

    public aemImagePrefix: string;
    public isBrowser: boolean;
    private _adaFriendlyNamePipe = inject(AdaFriendlyNamePipe);
    clickUrlMap = new Map();
    activePids: string[] = [];

    constructor(
        private pqpsUtilService: PqpsUtilService,
        private _offerUtilService: OfferUtilService,
        private _appConfigService: AppConfigService,
        private _mktEnvironmentService: MktEnvironmentService,
        @Inject(PLATFORM_ID) private _platformId: string,
    ) {
        this.isBrowser = isPlatformBrowser(this._platformId);
        this.aemImagePrefix = this._appConfigService.getAEMConfig()?.aemStaticAssetsUrl;
    }

    getDataBasedOnPageName(data: any, pageName: string, category = "explore") {
        // console.log(`>>> ${pageName}: Transforming data...`);
        let result = null;
        switch (pageName) {
            case 'explore':
                result = this.getExploreData(data, pageName);
                break;
            case 'vac':
                result = this.getVACData(data, pageName);
                break;
            default:
                result = this.getMPPData(data, pageName, category);
                break;
        }
        return result;
    }

    private getExploreData(data, pageName) {
        const sectionList = [{ ...data.featureCardData.item, template: 'MEDIUM_CC_DATA', defaultGridColumn: 3 }];
        data.marketingCardData.item.cardContainer.forEach(obj => sectionList.push({
            ...obj,
            template: 'SMALL_CC_DATA',
            defaultGridColumn: 4,
        }));
        this._offerUtilService.flowParams['afc'] = data?.PageInfo.item.afc;
        this.setOcParam(data);
        return ({
            PageInfo: data?.PageInfo?.item,
            pageName: pageName,
            heroBanner: {
                contentID: data?.banner?.items[0]?.contentId,
                heroBannerData: this.transformBannerData(data?.banner?.items[0]?.heroBannerData[0], data?.banner?.items[0]?.contentId,),
                cardArtData: this.getHeroCAData(data?.banner?.items[0]?.heroBannerData[0], data?.banner?.items[0]?.cardArtData[0])
            },
            navBarData: { classList: data?.navBarData?.item?.classList, "categoryTabs": data?.navBarData?.item?.categoryTabs.map(obj => ({ ...obj, iconName: this.getIcon(obj.categoryIconName), productList: this.filterActiveProducts(obj.productList) })), selectedNav: this.selectedNavbar(data?.navBarData?.item?.categoryTabs, 'explore') },
            legalBarData: { ...data?.legalBarData?.item, template: "cc_legal_bar_v1", lessText: (data?.legalBarData?.item?.lessText?.plaintext || "").trim(), moreText: (data?.legalBarData?.item?.moreText?.plaintext || "").trim() },
            cardData: [{
                template: 'MEDIUM_CC_DATA',
                defaultGridColumn: 3,
                slider: false,
                categoryHeader: {
                    ...data?.featureCardData?.item?.categoryHeader,
                    template: data?.featureCardData?.item?.categoryHeader?.template || 'cc_header_v1', headerLevel: '1',
                    headerLink: {
                        ...data?.featureCardData?.item?.categoryHeader?.headerLink,
                        text: this.replacePCnt(data?.featureCardData?.item?.categoryHeader?.headerLink.text, data?.navBarData, data.featureCardData.item.categoryHeader.categoryKey)
                    }
                },
                productList: this.transform(data?.featureCardData?.item?.productList, 'medium', pageName, data?.PageInfo?.item?.sectionOrderList, data?.engagementData?.items, data?.PageInfo?.item?.bannerPositions || [])
            },
            ...data?.marketingCardData?.item?.cardContainer.map(obj => ({
                template: 'SMALL_CC_DATA',
                defaultGridColumn: 4,
                slider: true,
                categoryHeader: {
                    ...obj.categoryHeader,
                    template: (obj.categoryHeader?.template || 'cc_header_v1'),
                    headerLevel: '2',
                    headerLink: {
                        ...obj.categoryHeader?.headerLink,
                        text: this.replacePCnt(obj?.categoryHeader?.headerLink?.text, data?.navBarData, obj?.categoryHeader?.categoryKey)
                    }
                },
                productList: this.transform(obj.productList, 'small', pageName, data?.PageInfo?.item?.sectionOrderList)
            }))
            ],
            engagementData: data.engagementContainer
        });
    }

    private getMPPData(data, pageName, category) {
        this.setOcParam(data);
        this._offerUtilService.flowParams['afc'] = data?.PageInfo?.item?.mppPageInfo?.afc;
        return ({
            PageInfo: data?.PageInfo?.item?.mppPageInfo,
            pageName: pageName,
            heroBanner: {
                contentID: data?.banner?.items[0]?.contentId,
                heroBannerData: this.transformBannerData(data?.mppBanner?.item?.mppPageInfo?.banner?.heroBannerData[0], data?.banner?.items[0]?.contentId, data?.CardData?.item?.productList),
                cardArtData: this.getHeroCAData(data?.mppBanner?.item?.mppPageInfo?.banner?.heroBannerData[0], data?.mppBanner?.item?.mppPageInfo?.banner?.cardArtData[0])
            },
            navBarData: { classList: data?.navBarData?.item?.classList, "categoryTabs": data?.navBarData?.item?.categoryTabs.map(obj => ({ ...obj, iconName: this.getIcon(obj.categoryIconName), productList: this.filterActiveProducts(obj.productList) })), selectedNav: this.selectedNavbar(data?.navBarData?.item?.categoryTabs, category) },
            legalBarData: { ...data?.legalBarData?.item, template: "cc_legal_bar_v1", lessText: (data?.legalBarData?.item?.lessText?.plaintext || "").trim(), moreText: (data?.legalBarData?.item?.moreText?.plaintext || "").trim() },
            cardData: [{
                template: 'LARGE_CC_DATA',
                defaultGridColumn: 2,
                categoryHeader: {
                    ...data.CardData?.item?.categoryHeader,
                    template: data?.CardData?.item?.categoryHeader?.template || 'cc_header_v1', headerLevel: '1',
                    headerLink: {
                        ...data.CardData?.item?.categoryHeader?.vacLink,
                        text: this.replacePCnt(data?.CardData?.item?.categoryHeader?.vacLink?.text, data?.navBarData, 'explore')
                }},
                productList: this.transform(data?.CardData?.item?.productList, 'large', pageName, data?.PageInfo?.item?.mppPageInfo?.sectionOrderList)
            }],
            engagementData: data.engagementContainer
        });
    }

    private getVACData(data, pageName) {
        this.setOcParam(data);
        this._offerUtilService.flowParams['afc'] = data?.PageInfo.item.afc;
        return ({
            PageInfo: data?.PageInfo?.item,
            pageName: pageName,
            cardData: [{
                template: 'SMALL_CC_DATA',
                defaultGridColumn: 4,
                slider: false,
                categoryHeader: {
                    ...data.CardData.item.categoryHeader,
                    template: data.CardData.item.categoryHeader.template || 'cc_header_v1', headerLevel: '1'
                }, //, heading: "Our Featured Cards", link : "View All (25) Citi Cards"
                chipsFilterData: { ...data.chipsFilterDatas.item, productCount: this.filterActiveProducts(data.CardData.item.productList).length },
                productList: this.transform(data.CardData.item.productList, 'small', pageName, data?.PageInfo?.item?.sectionOrderList, data?.PageInfo?.item?.defaultEngagementModules, data?.PageInfo.item.bannerPositions || [])
            }
            ]
        });

    }

    public transformBannerData(data, contentID = '', productList = []) {
        if (!data) return;
        if (data?.eyebrow) data.eyebrow.plaintext = data?.eyebrow?.plaintext?.trim();
        let backgroundEleStyle = this.getHeroBGStyle(data?.backgroundImage);
        let pid = '';
        data?.links.map((link) => {
            if (!!link.placeHolder && link.placeHolder.includes('additionalInfo-')) {
                pid = link.placeHolder.split('-')[1];
                productList.forEach(obj => {
                    const uniquePID = this.getUniqueId(obj.cardPid, obj?.cardMetadata?.productType);
                    if (uniquePID === pid) {
                        link.cardRef = obj.contentId;
                        link.cardName = (data?.eyebrow?.plaintext || obj.cardName.plaintext || "").trim();
                        link.pid = pid;
                        link.placeHolder = 'additionalInfo';

                    }
                });
            }
            link.bannerContentID = contentID;
            link.classList = 'text-white text-left';
        });
        data?.buttons?.map(buttonObj => {
            buttonObj.bannerContentID = contentID;
            return ({
                ...buttonObj,
                label: buttonObj?.label || buttonObj?.text || "",
                type: 'secondary',
                href: ((buttonObj?.placeHolder !== 'external') ? this.setPDPLink(buttonObj?.href) : buttonObj?.href) || null,
                placeHolder: buttonObj?.placeHolder?.replace('PID', pid),
                section: 'Hero'
            })
        })
        return ({
            ...data,
            eyebrow: !!data.eyebrow && (data.eyebrow.plaintext || "").trim(),
            heroHeading: !!data.heroHeading && (data.heroHeading.plaintext || "").trim(),
            heroDescription: !!data.heroDescription && (data.heroDescription.plaintext || "").trim(),
            heroBannerPriority: 'high',
            backgroundEleStyle: backgroundEleStyle,
            showCTA: data.showCta,
            items: data.buttons,
            links: data?.links
        })
    }

    private getHeroBGStyle(data: any): any {
        if (!data) return '';
        let bgStyle = {};
        if(data.split('||')?.length > 1){
            bgStyle = data.split('||').reduce((acc, path) => {
                const pathParts = path.trim().split(':');
                acc[pathParts?.[0]] = this.buildHeroBgStyle(pathParts?.[1]);
                return acc;
            },{});
        }else {
            bgStyle = {[data?.split(':')?.[0] || '4000']: this.buildHeroBgStyle(data?.split(':')[1] || '')};
        }
        return bgStyle;
    }
    public getHeroCAData(data, cardArtData) {
        if (!data || !cardArtData) return;
        let cardArtTarget = null;
        data?.buttons?.map((button) => {
            if (!['', null, undefined].includes(button?.href)) {
                cardArtData.navigationUrl = (button?.placeHolder !== 'external') ? this.setPDPLink(button?.href) : button?.href || null;
            }
            if (button.placeHolder === 'pdpPageLink') {
                cardArtTarget = button.target;
            }
        });
        return {
            ...cardArtData,
            'ariaLabel': this._adaFriendlyNamePipe.transform(data?.eyebrow.plaintext, 'ada') ?? cardArtData?.ariaLabel ?? '',
            'cardName': data?.eyebrow?.plaintext ?? cardArtData?.cardName ?? '',
            "priority": 'high',
            "cardArtUrl": this.aemImagePrefix + (cardArtData?.cardArtUrl?._path.replace("/content/dam/cfs/uspb/usmkt/cards/en/static/images", "") || ''),
            'linkPreventDefault': false,
            linkTarget: cardArtTarget || data?.buttons?.[0]?.target || '_self',
            cardArtSizes: { sm: '217x137', md: '296x187', lg: '261x165', xl: '306x195' }
        };
    }

    private transform(data, type, pageName, sectionOrderList, engagementData = [], bannerPosition = []) {
        // console.log(`>>> ${pageName}: Transforming data for ${type} module`);
        if (!data) return;
        sectionOrderList = this.getDefaultSectionOrderList(type, sectionOrderList);
        const playgroundData = this.getPlaygroundExistingData(`cc_module_${type}`);
        const filterActiveProducts = pageName != 'explore' ? this.filterActiveProducts(data) : data;
        let finalContent = filterActiveProducts.map((obj, index) => (this.transformCards(obj, type, pageName, playgroundData, index, sectionOrderList)));
        for (let i = 0; i < bannerPosition.length; i++) {
            const [, , , , productListIndex] = bannerPosition[i].sectionName.split('-').map(Number);
            finalContent.splice((parseInt(productListIndex) - 1), 0, this.transformEngagement(engagementData[i], pageName, 'engagement', productListIndex - 1));
        }
        return finalContent;
    }

    

    public transformEngagement(engagement: any, pageName, type = 'engagement', index): any {
        return {
            template: engagement.template,
            classList: "",
            type: type,
            items: [{
                active: true,
                contentId: engagement?.contentId,
                position: engagement?.position,
                eyebrow: engagement?.eyebrow?.plaintext,
                headerName: {
                    plaintext: engagement?.headerName?.plaintext,
                },
                headerLevel: engagement?.headerLevel,
                imageUrl: this.aemImagePrefix + ((engagement?.imageUrl?.path || '').replace("/content/dam/cfs/uspb/usmkt/cards/en/static/images", "")),
                frameImageUrl: engagement?.frameImageUrl?.map((img: string) => `${this.aemImagePrefix}${img}`).join(', '),
                featureBgUrl: this.aemImagePrefix + ((engagement?.featureBgUrl?.path || '').replace("/content/dam/cfs/uspb/usmkt/cards/en/static/images", "")),
                frameBgColor: this.buildHeroBgStyle(engagement?.frameBgColor || ''),
                featureCards: this.transformCards({ ...engagement?.featureCards[0], contentId: engagement?.contentId }, type, pageName, {}, index, []),
                featureRestatement: {},
                featureFamilyRedirect: {},
                featureAccels: []
            }],

        };
    }

    private transformCards(obj, type, pageName, playgroundData, index, sectionOrderList) {
        return {
            type: (type === 'engagement') ? 'engagement' : 'card',
            _variation: obj._variation,
            template: "cc_module_" + type,
            cardDetail: this.getCardDetail(obj, pageName),
            productChips: obj.chipReference || [],
            cardArtData: (!!obj.cardArtData) ? this.getCardArtData(obj, type, index) : {},
            rtbLineDescription: (obj.valueProp?.rtbLineDescription.plaintext || "").trim(),
            legalLinks: (!!obj.legalLinks) ? this.getLegalLinks(obj, obj?.contentId, type) : [],
            ctaData: (!!obj.ctaData) ? this.demoSetup(this.getCtaData(obj, obj?.contentId, type), playgroundData?.ctaData, `${obj.cardPid}|${playgroundData?.cardDetail?.PID}`) : {},
            flag: (!!obj.flag) ? this.demoSetup(this.getFlagData(obj), playgroundData?.flag, `${obj.cardPid}|${playgroundData?.cardDetail?.PID}`) : {},
            order: (sectionOrderList.keys().length > 0) ? { ...this.getSectionOrder(sectionOrderList), ...playgroundData?.order } : {},
            rtb: (!!obj.valueProp) ? this.demoSetup(this.getRtbData(obj, type), playgroundData?.rtb, `${obj.cardPid}|${playgroundData?.cardDetail?.PID}`) : {},
            ...this.addNewProduct(index, playgroundData)
        }
    }

    private demoSetup(actualData, playgroundData, pids) {
        if (!playgroundData) return actualData;
        const pidArr = pids.split("|");
        if (pidArr[1] === "") {
            return playgroundData;
        } else if (pidArr[0] === pidArr[1]) {
            return { ...actualData, ...playgroundData };
        } else {
            return actualData;
        }
    }
    private addNewProduct = (index, playgroundData) => ((index === 0 && playgroundData?.cardDetail?.PID === "NEW") ? playgroundData : {});

    private getDefaultSectionOrderList(type, sectionOrderList) {
        if (sectionOrderList.length === 0) {
            const DEFAULT_LARGE_ORDER = [
                { sectionName: "ctaContainer" },
                { sectionName: "rtbLineContainer" },
                { sectionName: "rtbContainer" }
            ];
            const DEFAULT_ORDER = [
                { sectionName: "capContainer" },
                { sectionName: "rtbContainer" },
                { sectionName: "ctaContainer" }
            ];
            return (type === 'large') ? DEFAULT_LARGE_ORDER : DEFAULT_ORDER;
        }
        return sectionOrderList;
    }

    private getCardDetail(obj, pageName) {
        return {
            ...obj?.cardMetadata,
            PID: obj?.cardPid,
            stackView: false,
            contentId: obj?.contentId,
            annualFee: (obj?.cardAnnualFee?.plaintext || "").trim(),
            cardDescription: (obj?.cardDescription?.plaintext || "").trim(),
            cardName: (obj?.cardName?.plaintext || "").trim(),
            headerClassList: obj?.headerClassList || 'cds-brand-vis',
            headerLevel: (pageName === 'mpp') ? '2' : '3',
            additionalInformationText: obj?.legalLinks?.filter(b => b?.placeHolder?.toLowerCase().includes('additionalinfo'))[0]?.text,
            pricingInfo: obj?.legalLinks?.filter(b => b?.placeHolder?.toLowerCase().includes('termsurl'))[0]?.text,
            pidPT: (!!obj?.cardMetadata?.productType) ? `${obj.cardPid}_${obj?.cardMetadata?.productType}` : obj?.cardPid
        };
    }

    private getCardArtData(obj, type, index) {
      const cardArtUrl = obj?.cardArtData?.cardArtUrl?._path ? this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, obj?.cardArtData?.cardArtUrl?._path) : this.aemImagePrefix + '/' + obj.cardMetadata?.pdpContentId + '/' + obj.cardMetadata?.pdpContentId + '_306x192.png';
        return {
            ...obj.cardArtData,
            cardName: (obj.cardName.plaintext || "").trim(),
            cardArtSize: { 'small': "sm", 'medium': 'md', 'large': 'lg' }[type],
            cardArtUrl: cardArtUrl,
            navigationUrl: this.setPDPLink(obj.ctaData?.cardDetailLinkContent?.href || ""),
            linkPreventDefault: true,
            tabIndex: -1,
            priority: (type !== "medium" && index < 4) ? "high" : "low"
        };
    }

    public getLegalLinks(obj, contentID, type) {
        return obj?.legalLinks?.map((legalLinkObj) => {
            if (type === "engagement") legalLinkObj.bannerContentID = contentID;
            const uniquePID = this.getUniqueId(obj.cardPid, (obj?.cardMetadata?.productType || ''))
            return ({
                ...legalLinkObj,
                pid: obj.cardPid,
                cardRef: obj.contentId,
                cardName: (obj.cardName.plaintext || "").trim(),
                placeHolder: !!legalLinkObj.placeHolder && legalLinkObj.placeHolder.replace('PID', uniquePID),
                section: obj.section
            })
        }
        );
    }

    public getCtaData(obj, contentID, type) {
        return {
            ...obj.ctaData,
            items: obj.ctaData?.button.map(buttonObj => {
                if (type === "engagement") buttonObj.bannerContentID = contentID;
                const uniquePID = this.getUniqueId(obj.cardPid, (obj?.cardMetadata?.productType || ''));
                let updatedButtonObj = { ...buttonObj }; 
                if (type === "pdp" && updatedButtonObj.text === "See if You're Pre-qualified" && updatedButtonObj.type === "secondary") {
                    updatedButtonObj.type = "tertiary";
                    this._offerUtilService.flowParams['oc'] = this._offerUtilService.flowParams['oc'] || obj?.cardMetadata['oc'] || '';
                    updatedButtonObj.iconName = "257-chevron-right";
                }
                return ({
                    ...updatedButtonObj,
                    ariaLabel: (updatedButtonObj?.ariaLabel || obj.cardName.plaintext || "").trim(),
                    label: updatedButtonObj.text,
                    classList: (updatedButtonObj?.classList || '') + ' ' + 'text-center',
                    placeHolder: !!updatedButtonObj.placeHolder && updatedButtonObj.placeHolder.replace('PID', uniquePID),
                    section: obj.section,
                    href: this.getPrequalURL(updatedButtonObj.placeHolder, updatedButtonObj.href) || null,
                })
            }),
            cardDetailLinkContent: { ...obj.ctaData?.cardDetailLinkContent, href: this.setPDPLink(obj.ctaData?.cardDetailLinkContent?.href) }
        };
    }

    private getFlagData(obj) {
        return {
            varient: null,
            classList: "",
            template: "cc_flag_v1",
            flagData: {
                ...obj.flag,
                headerLevel: 2, title: (obj.flag?.title.plaintext || "").trim()
            }
        };
    }

    getPrequalURL(placeholder: string, url: string | null): string | null {
        if (placeholder === 'prequalURL' && url) {
            const href = `${this._mktEnvironmentService.getEnvironmentVar('csrHostName')}${url}&OC=${this._offerUtilService.flowParams['oc']}`;
            return href;
        }
        return url;
    }

    private getSectionOrder(sectionOrderList) {
        return sectionOrderList.reduce((acc, item, index) => {
            acc[item.sectionName] = (index + 1).toString();
            return acc;
        }, {});
    }

    private getRtbData(obj, type) {
        type = (type === "engagement") ? "small" : type;
        return {
            template: "cc_rtb_" + type,
            stackView: (type == 'large') ? false : true,
            rtbLineDescription: (obj?.valueProp?.rtbLineDescription?.plaintext || "").trim(),
            rtbList: this.formatRtbData(obj?.valueProp, type),
            callOut: (obj?.valueProp?.callout || []).map(({ title, description }) => ({
                title: (title?.plaintext || "").trim(),
                description: (description?.plaintext || "").trim()
            }))
        };
    }

    private formatRtbData(data, type) {
        let results = [];
        switch (type) {
            case "small":
                (data.valuePropListSmall || []).forEach(item => { results.push({ "iconName": this.getIcon(item.iconName), "key": item.businessId, "description": (item.description.plaintext || "").trim() }) });
                break;
            case "medium":
                (data.valuePropListMedium || []).forEach(item => { results.push({ "iconName": this.getIcon(item.iconName), "key": item.businessId, "description": (item.description.plaintext || "").trim() }) });
                break;
            default:
                (data.valuePropListLarge || []).forEach(item => { results.push({ "iconName": this.getIcon(item.iconName), "key": item.businessId, "description": (item.description.plaintext || "").trim() }) })
        }
        return results;
    }

    getIcon(icon) {
        return this.isBrowser ? icon : 'pixel';
    }

    private selectedNavbar(navBarData = [], category = 'explore') {
        return navBarData.findIndex(obj => obj.categoryKey === category);
    }

    getPlaygroundExistingData(template = "") {
        if (!this.isBrowser) return {};
        const data = localStorage.getItem(template);
        let playgroundData = JSON.parse(data || '{}');
        return playgroundData[template] || {};
    }

    setPDPLink(link: any, flowParams?) {
        if (!link) return;
        if (!flowParams && this._offerUtilService.flowParams) flowParams = this._offerUtilService.flowParams;
        const sanatizedFlowParams = this._offerUtilService.sanitizeObject(flowParams);
        if (typeof link === 'string') {
            link = this.setParamsToURL(link, `category=${sanatizedFlowParams.category},intc=${sanatizedFlowParams.intc},afc=${sanatizedFlowParams.afc},flowId=${sanatizedFlowParams.flowId}`);
            return link;
        }
        link.href = this.setParamsToURL(link.href, `category=${sanatizedFlowParams.category},intc=${sanatizedFlowParams.intc},afc=${sanatizedFlowParams.afc},flowId=${sanatizedFlowParams.flowId}`);
        return link.href;
    }

    public getDynamicUrls({ priceObj, pageId, flowParams, mbolParams }) {
        if (!priceObj) return;
        const sanatizedFlowParams = this._offerUtilService.sanitizeObject(flowParams);
        const sanatizedFlowParamsTextFormat = Object.entries(sanatizedFlowParams)
            .map(([key, value]) => key + '=' + value)
            .join(',');
        // priceObj.uniqueId = `${priceObj.productIdentifier}-${priceObj.productType}`
        priceObj.pricingInfoLink = priceObj["terms-link"];
        flowParams.pid = priceObj.productIdentifier;

        switch (pageId) {
            case 'CITICRSNGA':
                if (priceObj.applyNowLink) {
                    priceObj.applyNowURL = priceObj.applyNowLink;
                }
                break;
            case 'CITIPQPSNGA':
                priceObj.applyNowURL = this.pqpsUtilService.getPQPSApplyNowUrl(priceObj["hashCode"], priceObj?.offerHashKey, sanatizedFlowParams.acquisitionType, sanatizedFlowParams?.invitationNumber, priceObj?.PID, sanatizedFlowParams?.aoOfferParams, sanatizedFlowParams?.oc);
                break;
        }
    }

    setParamsToURL = (url, value) => {
        const origin = this._mktEnvironmentService.getEnvironmentVar("cardsmkrHostName");
        let newUrl = new URL(url, origin);
        let params = value.split(",");
        params = params.filter(val => val.split('=')[1]);
        newUrl.search = `?${params.join('&')}`;
        return newUrl;
    };


    refreshPDPLink(pdplink, ctaData, cardArtData, priceObj, pageDataObj) {
        const updatedpdplink = this.setPDPLink(pdplink);
        if (ctaData?.cardDetailLinkContent) {
            ctaData.cardDetailLinkContent.href = updatedpdplink;
        }
        priceObj.pdpPageLink = updatedpdplink;
        cardArtData.navigationUrl = this.setPDPLink(cardArtData?.navigationUrl);
        if (pageDataObj?.heroBanner?.cardArtData) {
            pageDataObj?.heroBanner?.heroBannerData?.items?.map((t: any) => {
                if (t?.placeHolder == 'pdpPageLink') {
                    pageDataObj.heroBanner.cardArtData.navigationUrl = this.setPDPLink(pageDataObj.heroBanner.cardArtData.navigationUrl);
                    t.href = this.setPDPLink(t?.href);
                }
            });
        }
    }

    private replacePCnt(text: string, navBarData: any, categoryKey: string): string {
        if (!text && !navBarData && !categoryKey) return text;
        const pCount = this.filterActiveProducts(navBarData?.item?.categoryTabs.filter(({ categoryKey: ck }) => ck === categoryKey)?.[0]?.productList).length.toString();
        return text?.replace('XX', pCount) || text;
    }

    private setOcParam(data: any): void {
        if (!this._offerUtilService.flowParams['oc'] && (data?.PageInfo?.item?.oc || data?.PageInfo?.item?.mppPageInfo?.oc)) {
            this._offerUtilService.flowParams['oc'] = data?.PageInfo.item.oc || data?.PageInfo?.item?.mppPageInfo?.oc;
        }
    }

    private filterActiveProducts = (productList: any[]): string[] => productList.filter(({cardPid}) => this.activePids.includes(cardPid));    

    public getUniqueId = (PID: string, productType: string) => (!!productType) ? `${PID}_${productType}` : PID;

    private buildHeroBgStyle = (bgStyle: string): string => bgStyle.replaceAll('$$path$$', this.aemImagePrefix)?.trim() || '';
}
